import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { ICellEditorParams } from '@ag-grid-community/core';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    createComponent,
    EnvironmentInjector,
    OnDestroy,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { Destroyable, takeUntilDestroy } from '@frontend-site/shared/utils';
import {
    PFieldAbstractComponent,
    PFieldAutoCompleteComponent,
    PFieldDateComponent,
    PFieldMultiSelectComponent,
    PFieldSingleSelectComponent,
    PFieldUserComponent,
    PFieldUserAndGroupComponent
} from '@frontend-site/ui/components';
import { IPField } from '@panaya/ui/p-field';
import { PFieldTextComponent } from '@panaya/ui/p-field-text';
import { Observable, take } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'dynamic-editor-renderer',
    template: ` <div #container></div>`,
    styleUrls: ['./dynamic-p-field-cell-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicPFieldCellEditorComponent extends Destroyable implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
    @ViewChild('container', { read: ViewContainerRef })
    public container!: ViewContainerRef;
    private value = false;
    private inputs: Record<string, any> = {};
    private minMenuWidth = 230;
    private pFieldComponentType!: Type<IPField<any>>;
    private component!: ComponentRef<IPField<any>>;
    private params!: DynamicPFieldCellEditorParams<any, any>;
    private loadAfterViewInit = true;
    private readonly defaultMinMenuWidth = 230;

    constructor(private readonly environmentInjector: EnvironmentInjector, private readonly cd: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit(): void {
        if (!this.pFieldComponentType) {
            throw new Error('Please provide the expected PField type in cellEditorParams');
        }
        if (this.loadAfterViewInit) {
            this.loadComponent();
        }
    }

    private loadComponent(): void {
        this.component = createComponent(this.pFieldComponentType, { environmentInjector: this.environmentInjector });
        this.setComponentInputs(this.inputs);
        this.setComponentOutputs();
        this.container.insert(this.component.hostView);
        this.cd.detectChanges();
    }

    private setComponentInputs(inputs: Record<string, any>): void {
        this.component.instance.setValue(this.value);
        Object.entries(inputs)?.forEach(([key, value]) => {
            this.component.setInput(key, value);
        });
    }

    private setComponentOutputs(): void {
        this.component.instance.viewValueChanged.pipe(takeUntilDestroy(this)).subscribe((value) => this.onValueChanged(value));

        if (this.pFieldComponentType === PFieldAutoCompleteComponent && this.component.instance instanceof PFieldAutoCompleteComponent) {
            this.component.instance.optionMouseDown
                .pipe(
                    takeUntilDestroy(this),
                    tap((event: MouseEvent) => event.preventDefault()),
                    switchMap(() => {
                        return this.component.instance.viewValueChanged.pipe(take(1));
                    })
                )
                .subscribe((value) => {
                    this.onValueChanged(value);
                    this.params.api.stopEditing();
                });
        }
    }

    public agInit(params: DynamicPFieldCellEditorParams<any, any>): void {
        this.params = params;
        this.initValue();
        this.pFieldComponentType = params.pFieldComponentType;
        if (params.inputsFunction) {
            this.inputs = this.jsonObjectToRecord(params.inputsFunction(params.data));
        } else if (params.asyncInputs) {
            this.createComponentWithAsyncInputs(params.asyncInputs, params.data);
        } else if (params.inputs) {
            this.inputs = this.jsonObjectToRecord(params.inputs);
        }
        this.fitWidth(params);
    }

    private createComponentWithAsyncInputs(
        asyncInputs: NonNullable<DynamicPFieldCellEditorParams<any, any>['asyncInputs']>,
        data: any
    ): void {
        this.loadAfterViewInit = false;
        asyncInputs(data)
            .pipe(takeUntilDestroy(this))
            .subscribe((inputs) => {
                this.inputs = { ...this.inputs, ...inputs };
                if (!this.container) {
                    this.loadAfterViewInit = true;
                } else {
                    this.loadComponent();
                }
            });
    }

    private fitWidth(params: DynamicPFieldCellEditorParams<any, any>): void {
        const currentColumnWidth = +this.params.eGridCell.style.width.replace('px', '');
        this.minMenuWidth =
            params.minMenuWidth && params.minMenuWidth > this.defaultMinMenuWidth ? params.minMenuWidth : this.defaultMinMenuWidth;

        const isCurrentLessThanMin = currentColumnWidth < this.minMenuWidth;
        const isMinDefault = !(this.minMenuWidth > this.defaultMinMenuWidth);

        if (isCurrentLessThanMin && isMinDefault) {
            return;
        } else if (isCurrentLessThanMin) {
            this.changeWidth(this.minMenuWidth);
        } else {
            this.changeWidth(currentColumnWidth);
        }
    }

    private changeWidth(relevantWidth: number): void {
        const currentColumnWidthStyle = { width: relevantWidth + 'px', display: 'block' };

        if (
            this.pFieldComponentType == PFieldTextComponent ||
            this.pFieldComponentType == PFieldSingleSelectComponent ||
            this.pFieldComponentType == PFieldUserComponent ||
            this.pFieldComponentType == PFieldUserAndGroupComponent
        ) {
            this.inputs['dynamicStyle'] = currentColumnWidthStyle;
        }

        if (
            this.pFieldComponentType == PFieldSingleSelectComponent ||
            this.pFieldComponentType == PFieldUserComponent ||
            this.pFieldComponentType == PFieldUserAndGroupComponent ||
            this.pFieldComponentType == PFieldMultiSelectComponent
        ) {
            this.inputs['menuDynamicStyle'] = currentColumnWidthStyle;
            this.inputs['menuCustomClasses'] = 'max-width-100';
        }
    }

    public getValue(): any {
        return this.value;
    }

    public initValue(): void {
        if (this.params.getValueFunction) {
            this.value = this.params.getValueFunction(this.params.data);
        } else {
            this.value = this.params.value;
        }
    }

    private jsonObjectToRecord(jsonObject: JSON): Record<string, any> {
        return { ...(jsonObject || {}) };
    }

    public onValueChanged(value: any): void {
        this.value = value;
        if (
            this.pFieldComponentType == PFieldUserComponent ||
            this.pFieldComponentType == PFieldUserAndGroupComponent ||
            this.pFieldComponentType == PFieldSingleSelectComponent ||
            this.pFieldComponentType == PFieldDateComponent ||
            this.pFieldComponentType == PFieldMultiSelectComponent
        ) {
            this.params.api.stopEditing();
        }
        if (this.params.onValueChangedFunction) {
            this.params.onValueChangedFunction(this.params.data, value);
        }
    }

    public override ngOnDestroy(): void {
        this.component.destroy();
        super.ngOnDestroy();
    }
}

export interface DynamicPFieldCellEditorParams<TV, TD> extends ICellEditorParams<TV, TD> {
    getValueFunction?: (data: TD) => any;
    onValueChangedFunction?: (data: TD, value: TV) => any;
    pFieldComponentType: Type<PFieldAbstractComponent<any>>;
    inputs?: JSON;
    inputsFunction?: (data: TD) => JSON;
    asyncInputs?: (data: TD) => Observable<Record<string, any>>;
    minMenuWidth?: number;
}
